import { useMutation, useQueryClient } from "@tanstack/react-query";
import { processResponseWithApiProblemDetails } from "Infrastructure/Api/ApiUtils";
import {
  type CancelTransactionCommandResult,
  putTransactionsPublicIDCancel,
} from "Infrastructure/Api/Api";
import { toast } from "sonner";
import { Resources, useResource } from "Translations/Resources";
import { transactionStatusKeys } from "Api/Queries/Transactions/useTransactionStatus";
import { transactionsDetailKeys } from "Api/Queries/Transactions/useTransactionsDetail";
import { transactionsKeys } from "Api/Queries/Transactions/useTransactions";
import { transactionsRefundsKeys } from "Api/Queries/Transactions/useTransactionsRefunds";

type Props = {
  onSuccess?: (data: CancelTransactionCommandResult) => void;
  onError?: (error: unknown) => void;
};

export const useCancelTransactionMutation = ({
  onSuccess,
  onError,
}: Props = {}) => {
  const queryClient = useQueryClient();
  const { t } = useResource();

  return useMutation({
    mutationFn: processResponseWithApiProblemDetails((publicID: string) =>
      putTransactionsPublicIDCancel(publicID),
    ),
    onSuccess: (data: CancelTransactionCommandResult, publicID: string) => {
      queryClient.invalidateQueries({
        queryKey: transactionStatusKeys.byPublicId(publicID),
      });
      // ZS-1676: a storno of a paid transaction keeps the status Success — the outcome lives only in
      // the detail DTO (relatedTransactionStatus), so the detail cache must refetch too, otherwise an
      // employee staying on the detail page keeps seeing a plain successful payment.
      queryClient.invalidateQueries({
        queryKey: transactionsDetailKeys.all,
      });
      queryClient.invalidateQueries({
        queryKey: transactionsKeys.all,
      });
      queryClient.invalidateQueries({
        queryKey: transactionsRefundsKeys.all,
      });
      onSuccess?.(data);
    },
    onError: (error) => {
      toast.error(t(Resources.Transactions.TransactionCancelError));
      onError?.(error);
    },
  });
};
